// Set Piece Goals — half-donut widget (Phase 3 → M2 wave 4). Thin wrapper over
// <HalfDonut>.
//
// Two donuts (Scored, Conceded) for one team (the `.team-toggle`, defaulting to
// the fixture's home side). The slots never change, so — unlike First Goal — the
// mount is stable (no fixture key): a team/timeline change animates current→new,
// matching the legacy redraw. No stats row. Data now comes from app-api via
// `useWidgetData` (was the `getSetPieceData` legacyData getter); the shared
// shaper + delegated DOM are unchanged. Controls reshape client-side, no refetch.

import { shapeSetPiece } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import HalfDonut, { type DonutConfig } from "./HalfDonut.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface SetPieceProps {
  fixture: string;
  team?: string | null;
  timeline?: string | null;
}

export default function SetPiece({ fixture, team, timeline }: SetPieceProps) {
  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const home = (parts[0] ?? "").trim();
  const chosen = team || home;
  const { data, isPending, isError, refetch } = useWidgetData(
    "set-piece-goals",
    useFixtureKey(fixture)
  );

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const donuts: DonutConfig[] = shapeSetPiece(data?.[chosen], timeline).map(
    (d) => ({
      idNs: d.slot,
      dataValue: d.slot,
      label: d.slot,
      segments: d.segments,
      pct: d.pct,
      statsSpans: null,
    })
  );

  return (
    <HalfDonut dataAttr="slot" idPrefix="sp" hasStats={false} donuts={donuts} />
  );
}
